import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Clock, Plus, Trash2, AlertCircle } from "lucide-react";

// Common time slots presets
export const timePresets = {
  morning: { start: "08:00", end: "12:00" },
  afternoon: { start: "13:00", end: "17:00" },
  evening: { start: "18:00", end: "22:00" },
  fullDay: { start: "09:00", end: "18:00" },
};

export type TimeSlot = {
  id?: string;
  start: string;
  end: string;
};

type TimeSlotManagerProps = {
  selectedDay: number | null;
  timeSlots: TimeSlot[];
  onTimeSlotsChange: (slots: TimeSlot[]) => void;
  onSaveTimeSlots: () => void;
  dayNames: string[];
  isSaving: boolean;
};

const TimeSlotManager = ({
  selectedDay,
  timeSlots,
  onTimeSlotsChange,
  onSaveTimeSlots,
  dayNames,
  isSaving
}: TimeSlotManagerProps) => {
  
  // Generate time options every 15 minutes
  const timeOptions = React.useMemo(() => Array.from({ length: 24 * 4 }, (_, i) => {
    const hour = Math.floor(i / 4);
    const minute = (i % 4) * 15;
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  }), []);

  const handleTimeChange = (index: number, field: "start" | "end", value: string) => {
    const updatedTimeSlots = [...timeSlots];
    updatedTimeSlots[index][field] = value;
    onTimeSlotsChange(updatedTimeSlots);
  };

  const addTimeSlot = () => {
    // Add a new time slot with default values
    onTimeSlotsChange([...timeSlots, { start: "09:00", end: "18:00" }]);
  };

  const removeTimeSlot = (index: number) => {
    if (timeSlots.length > 1) {
      const updatedTimeSlots = [...timeSlots];
      updatedTimeSlots.splice(index, 1);
      onTimeSlotsChange(updatedTimeSlots);
    }
  };

  const applyPreset = (preset: keyof typeof timePresets) => {
    if (timeSlots.length === 1 && timeSlots[0].start === "09:00" && timeSlots[0].end === "18:00") {
      // If we have just the default slot, replace it
      onTimeSlotsChange([{ ...timePresets[preset] }]);
    } else {
      // Otherwise add a new slot with the preset
      onTimeSlotsChange([...timeSlots, { ...timePresets[preset] }]);
    }
  };

  return (
    <Card className="lg:col-span-3 bg-gradient-to-b from-[#1E293B] to-[#1A212E] border-[#2A3749] shadow-xl backdrop-blur-sm">
      <CardHeader className="pb-4">
        <CardTitle className="text-white text-xl">
          {selectedDay !== null 
            ? `Horários para ${dayNames[selectedDay]}` 
            : "Defina seus Horários"}
        </CardTitle>
        <CardDescription className="text-gray-400">
          {selectedDay !== null 
            ? "Configure os intervalos disponíveis" 
            : "Selecione um dia da semana"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {selectedDay !== null ? (
          <>
            <div className="bg-[#161D29]/80 rounded-lg p-4 border border-[#2A3749]">
              <h3 className="text-white text-sm font-medium mb-3">Presets de Horário</h3>
              <div className="grid grid-cols-4 gap-2">
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="bg-[#242C37] border-[#2A3749] text-gray-300 hover:bg-[#2D3748] hover:text-white"
                  onClick={() => applyPreset('morning')}
                >
                  Manhã
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="bg-[#242C37] border-[#2A3749] text-gray-300 hover:bg-[#2D3748] hover:text-white"
                  onClick={() => applyPreset('afternoon')}
                >
                  Tarde
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="bg-[#242C37] border-[#2A3749] text-gray-300 hover:bg-[#2D3748] hover:text-white"
                  onClick={() => applyPreset('evening')}
                >
                  Noite
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="bg-[#242C37] border-[#2A3749] text-gray-300 hover:bg-[#2D3748] hover:text-white"
                  onClick={() => applyPreset('fullDay')}
                >
                  Dia Todo
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              {timeSlots.map((slot, index) => (
                <div 
                  key={index} 
                  className="p-4 border border-[#2A3749] rounded-lg space-y-3 bg-gradient-to-r from-[#1A212E] to-[#222C3F] transition-all duration-200 hover:shadow-md"
                >
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-white font-medium flex items-center gap-2">
                      <Clock className="h-4 w-4 text-[#00B6F0]" />
                      Intervalo {index + 1}
                    </span>
                    {timeSlots.length > 1 && (
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="h-8 w-8 rounded-full text-red-400 hover:bg-red-900/30 hover:text-red-300"
                        onClick={() => removeTimeSlot(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm text-gray-400">Horário de Início</label>
                      <Select 
                        value={slot.start} 
                        onValueChange={(value) => handleTimeChange(index, "start", value)}
                      >
                        <SelectTrigger className="bg-[#242C37] border-[#2A3749] text-white">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#242C37] border-[#2A3749] max-h-[240px]">
                          {timeOptions.map((time) => (
                            <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm text-gray-400">Horário de Fim</label>
                      <Select 
                        value={slot.end} 
                        onValueChange={(value) => handleTimeChange(index, "end", value)}
                      >
                        <SelectTrigger className="bg-[#242C37] border-[#2A3749] text-white">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#242C37] border-[#2A3749] max-h-[240px]">
                          {timeOptions.map((time) => (
                            <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-2 space-y-4">
              <Button 
                variant="outline" 
                onClick={addTimeSlot}
                className="w-full border-dashed border-[#2A3749] text-gray-400 hover:bg-[#242C37] hover:text-white transition-all duration-200"
              >
                <Plus className="mr-2 h-4 w-4" /> Adicionar Intervalo
              </Button>
              
              <Button 
                onClick={onSaveTimeSlots}
                className="w-full bg-gradient-to-r from-[#00B6F0] to-[#0094C6] text-black font-medium hover:from-[#00A3D9] hover:to-[#0086B5] transition-all duration-200 shadow-md"
                disabled={isSaving}
              >
                <Clock className="mr-2 h-4 w-4" /> 
                {isSaving ? "Salvando..." : "Salvar Horários"}
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 px-8 text-center space-y-4 bg-[#161D29]/50 rounded-xl border border-dashed border-[#2A3749]">
            <AlertCircle className="h-12 w-12 text-[#00B6F0]/60 mb-2" />
            <p className="text-gray-400 max-w-xs">
              Selecione um dia da semana no painel à esquerda para configurar os horários disponíveis
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TimeSlotManager;
